import { useEffect, useId, useState } from 'react';
import { MoonIcon, SunnyIcon } from '@/icons/nav';
import {
  currentThemePreference,
  setThemePreference,
  THEME_PREFERENCES,
  watchTheme,
  type ThemePreference,
} from './theme';

const LABELS: Record<ThemePreference, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

/**
 * Light / Dark / System switch for the shell sidebar (both apps). Still a
 * native <select> with a real label (visible as a sun/moon glyph, read by
 * assistive tech as "Theme"): keyboard- and screen-reader-accessible with no
 * custom widget semantics to get wrong. `tone` matches the sidebar it sits
 * in — the business sidebar is a light surface, the admin one the ink bar.
 */
export function ThemeToggle({ tone = 'surface' }: { tone?: 'surface' | 'ink-bar' }) {
  const id = useId();
  const [pref, setPref] = useState<ThemePreference>(() => currentThemePreference());

  useEffect(() => watchTheme(setPref), []);

  const isDark =
    pref === 'dark' ||
    (pref === 'system' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);

  const wrapClass =
    tone === 'ink-bar'
      ? 'text-white/70 hover:bg-white/10 hover:text-white'
      : 'text-ink-2 hover:bg-surface-2 hover:text-ink';
  const selectClass =
    tone === 'ink-bar'
      ? 'bg-transparent text-white/90 border-transparent'
      : 'bg-transparent text-ink border-transparent';

  return (
    <span className={`flex items-center gap-md rounded-tile px-md py-[3px] ${wrapClass}`}>
      <label htmlFor={id} className="grid h-5 w-5 place-items-center" title="Theme">
        <span className="sr-only">Theme</span>
        {isDark ? <MoonIcon className="h-[18px] w-[18px]" aria-hidden="true" /> : <SunnyIcon className="h-[18px] w-[18px]" aria-hidden="true" />}
      </label>
      <select
        id={id}
        value={pref}
        onChange={(e) => {
          const next = e.target.value as ThemePreference;
          setPref(next);
          setThemePreference(next);
        }}
        className={`-ml-xs flex-1 cursor-pointer rounded-input border py-xs pl-xs text-body font-semibold ${selectClass}`}
        data-testid="theme-toggle"
      >
        {THEME_PREFERENCES.map((p) => (
          <option key={p} value={p} className="text-ink">
            {LABELS[p]}
          </option>
        ))}
      </select>
    </span>
  );
}
